import { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, MapPin, ExternalLink, Plane, Phone } from 'lucide-react';
import CategoryNav from './CategoryNav';
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from './NewsTicker';
import SocialLinks, { SOCIAL_FIELDS } from './SocialLinks';

const TRAVEL_FIELDS: FieldDef[] = [
  { key: "name", label: "Business Name", required: true },
  { key: "category", label: "Type", placeholder: "e.g. Air Tickets, Tour Packages, Visa Services" },
  { key: "phone", label: "Phone", placeholder: "e.g. 214-555-0100" },
  { key: "address", label: "Address", type: "textarea" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  ...SOCIAL_FIELDS,
];

// No seeded entries — the owner adds travel agent listings via Admin
// (AdminAddBar below). Every card added gets its own detail page at
// /travel-agents/:slug automatically.
export const TRAVEL_ENTRIES: any[] = [];

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=600&h=400&fit=crop';

const HERO_IMAGE =
  'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1600&h=500&fit=crop';

export function travelSlug(a: any, i: number) {
  return slugify(a.name || String(i));
}

function mapsUrl(entry: { name: string; address?: string }) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${entry.name}, ${entry.address || ''}`)}`;
}

export function TravelAgentCard({ entry, index, href }: { entry: { name: string; category?: string; address?: string; phone?: string; image?: string }; index: number; href?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col group"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="relative w-full h-36 overflow-hidden bg-gray-50">
        <img
          src={entry.image || FALLBACK_IMAGE}
          alt={entry.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
          }}
        />
        {entry.category && (
          <span className="absolute top-2 left-2 bg-[#082f6d]/90 text-white text-[10px] font-semibold px-2 py-1 rounded-full">
            {entry.category}
          </span>
        )}
      </div>
      <div className="flex flex-col flex-1 px-3 py-3 gap-1.5">
        <h3 className="font-semibold text-gray-900 leading-snug text-[14px]">
          {href ? (
            <a href={href} className="hover:text-[#f97316] transition-colors">
              {entry.name}
            </a>
          ) : (
            entry.name
          )}
        </h3>
        {entry.address && (
          <div className="flex items-start gap-1">
            <MapPin className="w-3 h-3 text-orange-500 shrink-0 mt-0.5" />
            <span className="text-[11px] text-gray-500 leading-snug">{entry.address}</span>
          </div>
        )}
        <div className="mt-auto pt-1.5 flex flex-col gap-1.5">
          <SocialLinks item={entry} variant="bar" />
          {entry.phone && (
            <a
              href={`tel:${entry.phone.replace(/[^\d+]/g, '')}`}
              className="inline-flex items-center justify-center gap-1 font-semibold text-white bg-[#082f6d] hover:bg-[#062454] rounded-lg transition-colors duration-200 px-3 py-1.5 text-[12px]"
            >
              <Phone className="w-3 h-3" /> {entry.phone}
            </a>
          )}
          <a
            href={mapsUrl(entry)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1 font-semibold text-white bg-[#f97316] hover:bg-[#ea580c] rounded-lg transition-colors duration-200 px-3 py-1.5 text-[12px]"
          >
            <ExternalLink className="w-3 h-3" /> View Location
          </a>
        </div>
      </div>
    </motion.div>
  );
}

export default function TravelAgents() {
  const [searchQuery, setSearchQuery] = useState('');

  const mc = useManagedCollection<any>("travel_agents", TRAVEL_ENTRIES, (a, i) => travelSlug(a, i));

  const filtered = mc.items.filter((a) => {
    const q = searchQuery.trim().toLowerCase();
    const matchesQuery =
      !q ||
      a.name.toLowerCase().includes(q) ||
      (a.address || '').toLowerCase().includes(q) ||
      (a.category || '').toLowerCase().includes(q);
    return matchesQuery;
  });

  // Respect the admin-managed order (drag-reorder) instead of alphabetical.
  const sorted = [...filtered].sort((a: any, b: any) => (a._order ?? 0) - (b._order ?? 0));

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative w-full" style={{ minHeight: "160px" }}>
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt="Airplane wing over the clouds"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/45" />
          <svg viewBox="0 0 1440 24" preserveAspectRatio="none" style={{ position: 'absolute', left: 0, bottom: '-1px', width: '100%', height: '18px', display: 'block' }}>
            <path d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z" fill="#ffffff" />
          </svg>
        </div>
        <div className="relative max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex items-center justify-center" style={{ zIndex: 10, minHeight: "160px" }}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="w-full max-w-[600px]">
            <div className="rounded-md" style={{ boxShadow: '0 0 40px 12px rgba(255,255,255,0.45), 0 0 80px 24px rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
              <div className="flex bg-white rounded-md shadow-md h-[40px]">
                <div className="flex-1 px-3 flex items-center min-h-[40px]">
                  <span className="text-[11px] text-gray-600 font-semibold shrink-0 mr-2 hidden sm:inline">Find agents</span>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="e.g. Air tickets, India tours, visa"
                    className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent"
                  />
                </div>
                <button className="bg-[#f97316] hover:bg-[#ea580c] text-white text-[13px] font-bold px-5 transition-colors duration-200 shrink-0 min-h-[40px] rounded-r-md">
                  <span className="flex items-center justify-center gap-1.5">
                    <Search className="w-3.5 h-3.5 sm:hidden" />
                    Search
                  </span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      <NewsTicker />

      {/* Secondary Category Nav */}
      <CategoryNav />

      {/* Listings */}
      <section className="bg-gray-50/50 py-8">
        <div className="max-w-[1320px] mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <h2 className="text-[18px] sm:text-[20px] md:text-[22px] font-bold text-gray-900 mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Indian Travel Agents in Dallas-DFW
            </h2>
            <p className="text-[12px] sm:text-[13px] text-gray-500 mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Desi travel specialists for trips to India, South Asia, and worldwide — air tickets, tour packages, visa services & more.
            </p>

            <AdminAddBar mc={mc} fields={TRAVEL_FIELDS} label="Add Travel Agent" />

            {sorted.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {sorted.map((entry, index) => (
                  <AdminItem key={entry._key} mc={mc} item={entry} fields={TRAVEL_FIELDS} title="Travel Agent">
                    <TravelAgentCard entry={entry} index={index} href={`/travel-agents/${entry._key}`} />
                  </AdminItem>
                ))}
              </div>
            )}

            {sorted.length === 0 && (
              <div className="text-center py-12">
                <Plane className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-400 text-[14px]">
                  No travel agent listings yet. Add the first one with the button above.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
